(function () {
  'use strict';

  const WRAPPER_SELECTOR = '.success-story-card-pill-wrapper';
  const PILL_SELECTOR = '.success-story-card-pill';
  const LIST_SELECTOR = '[data-success-story-list]';
  const GRID_SELECTOR = '[data-success-story-grid]';
  const LOAD_MORE_SELECTOR = '[data-load-more]';
  const LOAD_MORE_CONTAINER_SELECTOR = '[data-load-more-container]';
  const CARD_SELECTOR = '[data-success-story-card]';
  const CARD_LINK_SELECTOR = '.success-story-card-link';
  const EMPTY_SELECTOR = '.success-story-empty';
  const MIN_DURATION_SECONDS = 2;
  const MAX_DURATION_SECONDS = 8;
  const PIXELS_PER_SECOND = 36;
  let resizeObserver = null;

  function getDurationSeconds(overflow) {
    const seconds = overflow / PIXELS_PER_SECOND;
    return Math.min(MAX_DURATION_SECONDS, Math.max(MIN_DURATION_SECONDS, seconds));
  }

  function clearOverflowState(wrapper) {
    wrapper.removeAttribute('data-pill-overflow');
    wrapper.style.removeProperty('--pill-overflow');
    wrapper.style.removeProperty('--pill-slide-duration');
  }

  function updatePillOverflow(wrapper) {
    const pill = wrapper.querySelector(PILL_SELECTOR);
    if (!pill) {
      clearOverflowState(wrapper);
      return;
    }

    const styles = window.getComputedStyle(wrapper);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    const availableWidth = Math.max(0, wrapper.clientWidth - paddingLeft - paddingRight);
    const overflow = Math.ceil(pill.scrollWidth - availableWidth);
    if (overflow <= 0) {
      clearOverflowState(wrapper);
      return;
    }

    wrapper.setAttribute('data-pill-overflow', 'true');
    wrapper.style.setProperty('--pill-overflow', `${overflow}px`);
    wrapper.style.setProperty('--pill-slide-duration', `${getDurationSeconds(overflow)}s`);
  }

  function updateAllPills(root = document) {
    root.querySelectorAll(WRAPPER_SELECTOR).forEach(updatePillOverflow);
  }

  function markPillAsInteracted(event) {
    event.currentTarget.setAttribute('data-pill-interacted', 'true');
  }

  function initPillInteraction(root = document) {
    root.querySelectorAll(CARD_LINK_SELECTOR).forEach(link => {
      if (link.dataset.pillInteractionBound === 'true') return;

      link.dataset.pillInteractionBound = 'true';
      link.addEventListener('pointerenter', markPillAsInteracted, { passive: true, once: true });
      link.addEventListener('focusin', markPillAsInteracted, { passive: true, once: true });
      link.addEventListener('touchstart', markPillAsInteracted, { passive: true, once: true });
    });
  }

  function observeWrapper(wrapper) {
    if (!resizeObserver) return;
    resizeObserver.observe(wrapper);
  }

  function initObservers() {
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => updatePillOverflow(entry.target));
      });

      document.querySelectorAll(WRAPPER_SELECTOR).forEach(observeWrapper);
    } else {
      window.addEventListener('resize', () => updateAllPills(), { passive: true });
    }

    const list = document.querySelector(LIST_SELECTOR);
    if (!list || typeof MutationObserver === 'undefined') {
      return;
    }

    const mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (!(node instanceof Element)) return;

          if (node.matches(WRAPPER_SELECTOR)) {
            observeWrapper(node);
            updatePillOverflow(node);
            return;
          }

          node.querySelectorAll(WRAPPER_SELECTOR).forEach(observeWrapper);
          updateAllPills(node);
        });
      });
    });

    mutationObserver.observe(list, { childList: true, subtree: true });
  }

  function toInt(value, fallback = 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function initLoadMore() {
    const list = document.querySelector(LIST_SELECTOR);
    if (!list) return;

    const grid = list.querySelector(GRID_SELECTOR);
    const loadMoreButton = list.querySelector(LOAD_MORE_SELECTOR);
    let loadMoreContainer = list.querySelector(LOAD_MORE_CONTAINER_SELECTOR);
    if (!grid) return;

    let currentPage = toInt(list.dataset.paged, 1);
    let maxPages = toInt(list.dataset.maxPages, 1);
    let nextPageUrl = list.dataset.nextPageUrl || '';
    let isLoading = false;

    function hasMore() {
      if (nextPageUrl) return true;
      return currentPage < maxPages;
    }

    function updateUi() {
      const shouldShow = hasMore();

      if (!shouldShow && !isLoading && loadMoreContainer) {
        loadMoreContainer.remove();
        loadMoreContainer = null;
      }

      if (loadMoreButton) {
        loadMoreButton.disabled = isLoading || !shouldShow;
        loadMoreButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        loadMoreButton.classList.toggle('is-loading', isLoading);
      }
    }

    function appendCardsFromResponse(doc) {
      const incomingList = doc.querySelector(LIST_SELECTOR);
      if (!incomingList) return false;

      const incomingGrid = incomingList.querySelector(GRID_SELECTOR);
      if (!incomingGrid) return false;

      const cards = incomingGrid.querySelectorAll(CARD_SELECTOR);
      if (!cards.length) return false;

      const emptyState = grid.querySelector(EMPTY_SELECTOR);
      if (emptyState) {
        emptyState.remove();
      }

      cards.forEach(card => {
        grid.appendChild(card);
      });

      initPillInteraction(grid);

      currentPage = toInt(incomingList.dataset.paged, currentPage + 1);
      maxPages = toInt(incomingList.dataset.maxPages, maxPages);
      nextPageUrl = incomingList.dataset.nextPageUrl || '';
      list.dataset.paged = String(currentPage);
      list.dataset.maxPages = String(maxPages);
      list.dataset.nextPageUrl = nextPageUrl;

      return true;
    }

    async function loadNextPage() {
      if (isLoading || !hasMore()) {
        return;
      }

      const pageUrl = nextPageUrl || '';
      if (!pageUrl) {
        return;
      }

      isLoading = true;
      updateUi();

      try {
        const response = await fetch(pageUrl, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        appendCardsFromResponse(doc);
      } catch (error) {
        // Keep current pagination state on failure so user can retry.

        console.error('Failed to load more success stories:', error);
      } finally {
        isLoading = false;
        updateUi();
      }
    }

    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', () => {
        loadMoreButton.classList.add('is-clicked');

        window.setTimeout(() => {
          loadMoreButton.classList.remove('is-clicked');
        }, 160);

        loadNextPage();
      });
    }

    updateUi();
  }

  function init() {
    initPillInteraction();
    updateAllPills();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => updateAllPills());
    }

    window.addEventListener('load', () => updateAllPills(), { once: true });
    initObservers();
    initLoadMore();
  }

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
